import {
  Button,
  DateTimePicker,
  DurationPicker,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { PiPauseBold, PiPlayBold, PiStopFill } from 'react-icons/pi';
import { cn, useTimeZoneContext } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useReplayContext } from './ReplayContext';
import { useReplayRange } from '../../../../../libs/websockets/src/lib/replay-server/useReplayRange';
import { useFilterContext } from '../Map/FilterContext';
import { useSetMapLegendItems } from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';
import { useIsReplayDisabled } from './useIsReplayDisabled';
import { useStartStopPauseReplay } from './usePlayReplay';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

interface ReplaySpeedPickerProps {
  disabled: boolean;
  replaySpeed: number;
  setReplaySpeed: (replaySpeed: number) => void;
}

const ReplaySpeedPicker = ({
  disabled,
  replaySpeed,
  setReplaySpeed,
}: ReplaySpeedPickerProps) => {
  const { messages } = useLocalization();

  return (
    <Select
      disabled={disabled}
      value={replaySpeed.toString()}
      onValueChange={(value: string) => setReplaySpeed(parseInt(value))}
    >
      <SelectTrigger className="w-[200px]">
        <SelectValue placeholder="Select a replay speed" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="1">{messages.replaySpeedNormal}</SelectItem>
        <SelectItem value="2">{messages.replaySpeedSlightlyFast}</SelectItem>
        <SelectItem value="4">{messages.replaySpeedFast}</SelectItem>
        <SelectItem value="8">{messages.replaySpeedVeryFast}</SelectItem>
        <SelectItem value="16">{messages.replaySpeedExtremelyFast}</SelectItem>
        <SelectItem value="32">{messages.replaySpeedUltraFast}</SelectItem>
        <SelectItem value="64">{messages.replaySpeedHyperSpeed}</SelectItem>
      </SelectContent>
    </Select>
  );
};

function formatTime(seconds: number) {
  const pad = (num: number) => num.toString().padStart(2, '0');

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(remainingSeconds)}`;
  } else {
    return `${minutes}:${pad(remainingSeconds)}`;
  }
}

export const Replay = React.memo(() => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { data: replayRange } = useReplayRange();
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();
  const { applyFilter } = useFilterContext();
  const { settings } = useSettings();

  const { startReplay, stopReplay, pauseReplay } = useStartStopPauseReplay();

  const { viewMode } = useViewMode();
  const {
    isPlaying,
    secondsSinceStart,
    setSecondsSinceStart,
    replaySpeed,
    setReplaySpeed,
    maxTimeInSeconds,
    resetReplay,
    setHasLoaded,
    startDate,
    setStartDate,
    duration,
    setDuration,
    currentTime,
  } = useReplayContext();

  const { isDisabled, disableReason } = useIsReplayDisabled();
  const { mutate: updateLegend } = useSetMapLegendItems(isPlaying);

  const handleSliderChange = useCallback(
    (value: number[]) => {
      setSecondsSinceStart(value[0]);
      setHasLoaded(false);
    },
    [setSecondsSinceStart, setHasLoaded]
  );

  useEffect(() => {
    if (isPlaying) {
      applyFilter(false);
      updateLegend({
        mapLegendItems:
          settings['ribbonPlugin.settings']?.ship_color_noshow_sel || [],
        showItem: false,
      });
    }
  }, [applyFilter, isPlaying, settings, updateLegend]);

  const onPlayPause = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (isPlaying) {
      pauseReplay();
    } else {
      startReplay();
    }
  }, [isDisabled, isPlaying, pauseReplay, startReplay]);

  const timeDisplay = useMemo(() => {
    return `${formatTime(secondsSinceStart)} / ${formatTime(maxTimeInSeconds)}`;
  }, [secondsSinceStart, maxTimeInSeconds]);

  const onChangeStartDate = useCallback(
    (date: Date | undefined) => {
      setStartDate(date);
      resetReplay({ resetDate: false });
    },
    [resetReplay, setStartDate]
  );

  return (
    <div className="space-y-8">
      <div className="flex space-x-4">
        <DateTimePicker
          className="w-[340px]"
          date={startDate}
          onChange={onChangeStartDate}
          timeZone={timeZone}
          dateTimeFormat={userDateFormat}
          dateLimit="past"
          minDate={replayRange?.startDatetime}
          maxDate={replayRange?.endDatetime}
        />
        <DurationPicker value={duration} onChange={setDuration} />
      </div>

      <Slider
        value={[secondsSinceStart]}
        disabled={viewMode !== ViewMode.Replay}
        onValueChange={handleSliderChange}
        max={maxTimeInSeconds}
        step={1}
        className="w-full"
      />

      <div className="flex space-x-4 items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="justify-center"
              disabled={viewMode !== ViewMode.Replay}
              onClick={stopReplay}
            >
              {viewMode === ViewMode.Replay ? (
                <PiStopFill className="h-6 w-6 text-red-500" />
              ) : (
                <PiStopFill className="h-6 w-6" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{messages.replayStopTooltip}</TooltipContent>
        </Tooltip>
        {isDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={cn('justify-center opacity-50 cursor-default')}
                onClick={onPlayPause}
              >
                {!isPlaying ? (
                  <PiPlayBold className="h-6 w-6" />
                ) : (
                  <PiPauseBold className="h-6 w-6" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{disableReason}</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="justify-center"
                onClick={onPlayPause}
              >
                {!isPlaying ? (
                  <PiPlayBold className="h-6 w-6" />
                ) : (
                  <PiPauseBold className="h-6 w-6" />
                )}
              </Button>
            </TooltipTrigger>
            {isPlaying && (
              <TooltipContent>{messages.replayPauseTooltip}</TooltipContent>
            )}
          </Tooltip>
        )}
        <ReplaySpeedPicker
          disabled={viewMode !== ViewMode.Replay}
          replaySpeed={replaySpeed}
          setReplaySpeed={setReplaySpeed}
        />
        <div className="flex flex-col space-y-2">
          <Label className={cn(viewMode !== ViewMode.Replay && 'opacity-50')}>
            {formatDateTimeWithTimeZone(currentTime)}
          </Label>
          <Label className={cn(viewMode !== ViewMode.Replay && 'opacity-50')}>
            {timeDisplay}
          </Label>
        </div>
      </div>
    </div>
  );
});

Replay.displayName = 'Replay';
